import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FormSummary, getFormAccess, querySubmissionsApi, submissionQueryString, updateFormApi } from '../../api/forms';
import { FolderSummary, listFoldersApi, moveFormsApi } from '../../api/access';
import type { FormAccess } from '../../shared/types';
import { useToast } from '../../components/Toast';
import { useDirty, useUnsavedGuard } from '../../components/unsaved';
import { useFormWorkspace } from './FormWorkspace';
import UploadLimitsFields, { UploadLimitsValue, uploadLimitsFromMeta, uploadLimitsToMeta } from './UploadLimitsFields';
import { resolveUploadLimits } from '../../form-engine/uploadLimits';
import { RowCard, SaveBar, Toggle } from '../../components/fs';
import DuplicateFormDialog from './DuplicateFormDialog';
import DeleteFormModal from './DeleteFormDialog';

/** The retention floor the server enforces (`sanitizeRetention`): under it a value is dropped, so the page refuses it first. */
export const RETENTION_MIN_DAYS = 7;

/**
 * What the form records about who answered, in one line (screen 22's
 * "Identity" row). It is decided on Link & access, so the row reads it and
 * points there rather than growing a second control.
 */
export function identityWords(mode: FormAccess['mode'] | null): string {
  switch (mode) {
    case 'sso': return 'Whatever sign-in gives us — name and email, verified by the provider';
    case 'magic_link': return 'A confirmed email address';
    case 'invite': return 'The person each invitation was sent to';
    case 'password': return 'Not captured — the form asks for a password, not a name';
    case 'open': return 'Not captured — anyone with the link can respond';
    default: return '…';
  }
}

/**
 * Per-form settings (R8, screen 22): General · Responses · Branding, saved
 * together, with the save bar up only while something has changed.
 *
 * Deliberately NOT here any more:
 *  - appearance and the submit button label, which are edited on the canvas
 *    where you can see them, and ride the draft like the rest of the form;
 *  - who can open the form, and when it opens and closes, which are both the
 *    same question and both live on Link & access;
 *  - whether the form is taking submissions at all: Disable form lives on
 *    Link & access beside the schedule and the audience that qualify it;
 *  - the two built-in emails, which are ordinary Email steps on Workflow.
 *
 * What is left is operational rather than part of the form definition, so it
 * saves live. The Form-Magic badge sits here for that reason: it is not
 * something the owner styles, and a form that has just gone onto a paid plan
 * should lose the badge without a republish. The folder is new here (R8):
 * moving a form is the same call the forms list's Move to… makes.
 */
export default function FormSettings() {
  const { form, refreshForm, features } = useFormWorkspace();
  const navigate = useNavigate();
  const [name, setName] = useState(form.name);
  const [slug, setSlug] = useState(form.slug);
  const [folderId, setFolderId] = useState<number | null>(form.folder_id ?? null);
  const [folders, setFolders] = useState<FolderSummary[] | null>(null);
  const [uploadsEnabled, setUploadsEnabled] = useState(form.settings.uploads?.enabled !== false);
  /** the form-wide limits; a file field's own panel can tighten them further */
  const [uploadLimits, setUploadLimits] = useState<UploadLimitsValue>(() => uploadLimitsFromMeta(form.settings.uploads));
  /** how required fields are marked on the public form; absent = the asterisk + "(optional)" every form had before U3 */
  const [requiredMarker, setRequiredMarker] = useState<'asterisk' | 'label'>(form.settings.requiredStyle?.marker === 'label' ? 'label' : 'asterisk');
  const [optionalSuffix, setOptionalSuffix] = useState(form.settings.requiredStyle?.optionalSuffix !== false);
  /** D15 (R9): the summary of what to fix at the top of the page; absent = off, the form as it was */
  const [errorSummary, setErrorSummary] = useState(form.settings.errorSummary === true);
  /**
   * Hide the Form-Magic badge. The stored preference survives a downgrade, so
   * a form can hold `true` on a plan that no longer honours it — the checkbox
   * shows what is stored either way, and the hint below says whether it is in
   * force. The server pins it on a plan without the feature.
   */
  const [hideAd, setHideAd] = useState(!!form.settings.hideAdvertisement);
  /** retention (T4): off = keep forever; on = delete rows AND files after N days */
  const [retentionOn, setRetentionOn] = useState(!!form.settings.retention);
  const [retentionDays, setRetentionDays] = useState(String(form.settings.retention?.days ?? 90));
  const [accessMode, setAccessMode] = useState<FormAccess['mode'] | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [deleting, setDeleting] = useState<{ submission_count: number } | null>(null);
  const toast = useToast();

  /**
   * Everything on this screen that a Save would send. These settings are
   * operational rather than part of the draft, so nothing else is watching
   * them — leave with the notification address half-typed and it is gone.
   *
   * It has to list every field the save posts. A field added to the body but
   * not to this object is one the guard calls unchanged, so leaving the screen
   * throws it away without asking — which is exactly what happened to the
   * badge switch when it and the guard landed in the same file on the same
   * day and merged without a conflict.
   */
  const snapshot = useMemo(
    () => ({ name, slug, folderId, uploadsEnabled, uploadLimits, hideAd, requiredMarker, optionalSuffix, errorSummary, retentionOn, retentionDays }),
    [name, slug, folderId, uploadsEnabled, uploadLimits, hideAd, requiredMarker, optionalSuffix, errorSummary, retentionOn, retentionDays]
  );
  const { dirty, markSaved } = useDirty(snapshot);
  const guard = useUnsavedGuard({
    dirty, busy: saving,
    what: 'Your changes to this form’s settings',
    onSave: () => save(),
  });

  /** Everything back to what the form holds — the save bar's Discard. */
  const reset = useCallback(() => {
    setName(form.name); setSlug(form.slug); setFolderId(form.folder_id ?? null);
    setUploadsEnabled(form.settings.uploads?.enabled !== false);
    setUploadLimits(uploadLimitsFromMeta(form.settings.uploads));
    setRequiredMarker(form.settings.requiredStyle?.marker === 'label' ? 'label' : 'asterisk');
    setOptionalSuffix(form.settings.requiredStyle?.optionalSuffix !== false);
    setErrorSummary(form.settings.errorSummary === true);
    setHideAd(!!form.settings.hideAdvertisement);
    setRetentionOn(!!form.settings.retention);
    setRetentionDays(String(form.settings.retention?.days ?? 90));
    setError('');
  }, [form]);

  // Another form in the same mounted screen takes its own name, slug and
  // folder. Not on mount: the state above already holds them, and a keystroke
  // that lands before this effect runs would be overwritten by the very value
  // it replaced. The old useDirty baseline hid that loss by calling it an
  // unsaved change; since the R12 follow-up it would read clean.
  const shownFormId = useRef(form.id);
  useEffect(() => {
    if (shownFormId.current === form.id) return;
    shownFormId.current = form.id;
    setName(form.name); setSlug(form.slug); setFolderId(form.folder_id ?? null);
  }, [form.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let live = true;
    // The folder list and the access mode are read-once facts for two rows;
    // neither failing takes the page down — the row simply says less.
    listFoldersApi().then((r) => { if (live) setFolders(r.folders); }).catch(() => { if (live) setFolders([]); });
    getFormAccess(form.id).then((r) => { if (live) setAccessMode(r.effectiveMode || r.access.mode); }).catch(() => undefined);
    return () => { live = false; };
  }, [form.id]);

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    // `save` rethrows for the leave dialog's benefit; pressing the button is
    // the ordinary path, and the failure is already on screen.
    await save().catch(() => undefined);
  }

  const retentionTooShort = retentionOn && !(Math.floor(Number(retentionDays)) >= RETENTION_MIN_DAYS);

  async function save() {
    // The server drops a retention under 7 days and stores nothing (R0 found
    // the page then toasted "saved" with the switch still on) — so the page
    // refuses first and says why.
    if (retentionTooShort) {
      const msg = `Keep each response for at least ${RETENTION_MIN_DAYS} days, or switch automatic deletion off.`;
      setError(msg);
      throw new Error(msg);
    }
    setSaving(true); setError('');
    try {
      // The folder is the forms list's own move, made first so a refused move
      // (a folder this person cannot reach) stops the save before it writes.
      if (folderId !== (form.folder_id ?? null)) await moveFormsApi([form.id], folderId);
      // No `status`: the only way to a published form is to publish it, and
      // whether a published form is COLLECTING is the switch on Access.
      //
      // No `notifications` / `autoresponder` either. The server reads an
      // absent key as "keep what is stored", so dropping them here does not
      // clear anybody's data.
      //
      // No `document` key, for the same reason: the form's PDF is edited on
      // its own tab, and saving Settings must not reach across and clear it.
      await updateFormApi(form.id, {
        name, slug,
        settings: {
          uploads: { enabled: uploadsEnabled, ...(uploadLimitsToMeta(uploadLimits) || {}) },
          requiredStyle: { marker: requiredMarker, optionalSuffix },
          // `false` clears the key server-side, so an absent key keeps meaning "off".
          errorSummary,
          hideAdvertisement: hideAd,
          // null clears the key server-side (the sanitizer drops anything
          // that is not ≥ 7 whole days).
          retention: retentionOn ? { days: Math.floor(Number(retentionDays)) } : null,
        },
      });
      await refreshForm();
      markSaved();
      toast('Settings saved — in force now.');
    } catch (err: any) {
      setError(err.message || 'Save failed');
      // Rethrown so "Save and leave" stays put on a failure rather than
      // leaving with the very changes the save could not store.
      throw err;
    } finally {
      setSaving(false);
    }
  }

  /** Delete asks for the name once the form holds responses; the count includes the spam bin. */
  async function askDelete() {
    try {
      const r = await querySubmissionsApi(form.id, submissionQueryString({
        limit: 1, conditions: [{ column: 'meta:triageStatus', op: 'notempty' }],
      }));
      setDeleting({ submission_count: r.total });
    } catch {
      setDeleting({ submission_count: 0 });
    }
  }

  /** No plan feature: the switch is shown, disabled, with what lifts it. */
  const adOff = !features.hideAdvertisement;
  const asSummary = form as unknown as FormSummary;

  // noValidate: the page says why a value is refused (retention under 7) in
  // its own words, not the browser's bubble.
  return (
    <form onSubmit={onSave} className="fst-page" noValidate>
      <div className="fs-page-head">
        <div className="text">
          <h1>Form settings</h1>
          <div className="fs-page-sub">
            How this form is addressed, and what it allows. These take effect as soon as you
            save — <b>they are not part of the draft</b>. Email is sent by steps on{' '}
            <Link to="../workflow">Workflow →</Link>; whether the form is taking responses at
            all lives on <Link to="../access">Link &amp; access →</Link>.
          </div>
        </div>
      </div>

      {error && <div className="ad-err">{error}</div>}

      <div className="fst-cards">
        <section className="fs-card fst-card" aria-labelledby="fst-general">
          <h2 id="fst-general">General</h2>
          <RowCard label={<label htmlFor="fs-name">Name</label>} hint="Internal — respondents see the header title set on Fields.">
            <input id="fs-name" className="fs-input" value={name} onChange={(e) => setName(e.target.value)} />
          </RowCard>
          <RowCard label={<label htmlFor="fs-slug">Address</label>} hint="Changing the address changes the public link — old links stop working.">
            <div className="fs-prefixed">
              <span className="prefix">/f/</span>
              <input id="fs-slug" className="fs-input" value={slug} onChange={(e) => setSlug(e.target.value)} />
            </div>
          </RowCard>
          {folders && (
            <RowCard label={<label htmlFor="fs-folder">Folder</label>} hint="Where it sits in the list, and whose access it inherits.">
              <select
                id="fs-folder" className="fs-input" value={folderId ?? ''}
                onChange={(e) => setFolderId(e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">Unfiled</option>
                {folders.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
                {/* A folder this person cannot list stays selectable, so the row still says where the form is. */}
                {folderId && !folders.some((f) => f.id === folderId) && (
                  <option value={folderId}>{form.folder_name || `Folder ${folderId}`}</option>
                )}
              </select>
            </RowCard>
          )}
        </section>

        <section className="fs-card fst-card" aria-labelledby="fst-responses">
          <h2 id="fst-responses">Responses</h2>
          <RowCard label="Identity" hint="What the form records about who answered." actions={<Link className="fs-btn text sm" to="../access">Change on Link &amp; access →</Link>}>
            <span className="fst-value" data-testid="identity-words">{identityWords(accessMode)}</span>
          </RowCard>
          <RowCard label={<label htmlFor="fs-required-marker">Required fields show</label>} hint="How the form tells respondents what they must answer.">
            <select id="fs-required-marker" className="fs-input" value={requiredMarker} onChange={(e) => setRequiredMarker(e.target.value === 'label' ? 'label' : 'asterisk')}>
              <option value="asterisk">An asterisk after the label (Email *)</option>
              <option value="label">The word “Required” after the label</option>
            </select>
            <label className="fs-check fst-check">
              <input type="checkbox" checked={optionalSuffix} onChange={(e) => setOptionalSuffix(e.target.checked)} />
              <span>
                <b>Show “(optional)” after fields that are not required</b>
                <small>Turn this off on a form where nearly everything is required, so the marks stay on the few that are.</small>
              </span>
            </label>
          </RowCard>
          <RowCard label="Errors" hint="What respondents see when Continue or Submit is refused.">
            <div className="fst-toggle-row">
              <Toggle id="fs-error-summary" checked={errorSummary} onChange={setErrorSummary} label="Show a summary of what to fix at the top of the page" />
              <span className="fst-toggle-hint">
                Helps people using screen readers and long pages: the box takes focus, and each item is a link to its field.
                Every field still shows its own message. Off, the form lists what to fix above the button, as it always has.
              </span>
            </div>
          </RowCard>
          <RowCard label="Uploads" hint="Applies to every file field on this form at once.">
            <div className="fst-toggle-row">
              <Toggle id="fs-uploads" checked={uploadsEnabled} onChange={setUploadsEnabled} label="Allow file uploads on this form" />
              <span className="fst-toggle-hint">Turning this off refuses new attachments; files already submitted are kept.</span>
            </div>
            {uploadsEnabled && (
              <div className="fst-limits">
                <UploadLimitsFields
                  value={uploadLimits} onChange={setUploadLimits} variant="fs" idPrefix="fs-up"
                  inherited={resolveUploadLimits(undefined, undefined)}
                  inheritLabel="Any common document, image or archive"
                />
                <div className="hint">
                  Respondents see these limits on the field and are told at once when a file is over them.
                  A single file field can tighten them further from its panel on Fields.
                </div>
              </div>
            )}
          </RowCard>
          <RowCard label="Keep for" hint="How long this form keeps what people submit.">
            <div className="fst-toggle-row">
              <Toggle id="fs-retention" checked={retentionOn} onChange={setRetentionOn} label="Delete responses automatically" />
              <span className="fst-toggle-hint">
                Off = kept forever (your plan&rsquo;s record cap still applies). On = each response is{' '}
                <b>permanently deleted</b> — answers, uploaded files and signatures — once it is older than the window.
                There is no undo.
              </span>
            </div>
            {retentionOn && (
              <div className={`fst-days${retentionTooShort ? ' bad' : ''}`}>
                <label htmlFor="fs-retention-days">Keep each response for</label>
                <div className="fs-prefixed">
                  <input
                    id="fs-retention-days" className="fs-input" type="number" min={RETENTION_MIN_DAYS} step={1} value={retentionDays}
                    aria-invalid={retentionTooShort || undefined}
                    onChange={(e) => setRetentionDays(e.target.value)}
                  />
                  <span className="prefix" style={{ order: 2 }}>days</span>
                </div>
                <div className="hint">
                  {retentionTooShort
                    ? `At least ${RETENTION_MIN_DAYS} days — a shorter window cannot be saved.`
                    : `At least ${RETENTION_MIN_DAYS}. The sweep runs daily; deletion includes every stored file.`}
                </div>
              </div>
            )}
          </RowCard>
        </section>

        <section className="fs-card fst-card" aria-labelledby="fst-branding">
          <h2 id="fst-branding">Branding</h2>
          <RowCard
            label="Form-Magic badge"
            hint="A small “Powered by Form-Magic.com” link under the form — the only thing on a public form that is ours rather than yours."
          >
            <label className="fs-check fst-check" style={adOff ? { opacity: 0.6 } : undefined}>
              <input
                type="checkbox" checked={hideAd} disabled={adOff}
                onChange={(e) => setHideAd(e.target.checked)}
              />
              <span>
                <b>Hide the badge on this form</b>
                <small>
                  {adOff
                    ? 'Removing the badge is on the paid plans. Everything else on this page stays yours on every plan.'
                    : 'Respondents see no mention of Form-Magic. Applies to this form only.'}
                </small>
              </span>
            </label>
            {adOff && (
              <div className="hint fst-hint"><Link to="/admin/settings/billing/plans">See the plans →</Link></div>
            )}
            {!adOff && hideAd && !form.settings.hideAdvertisement && (
              <div className="hint fst-hint">Save to apply — the badge disappears on the next page load.</div>
            )}
          </RowCard>
        </section>

        <div className="fst-danger">
          <button type="button" className="fs-btn neutral" onClick={() => setDuplicating(true)}>Duplicate form</button>
          <button type="button" className="fs-btn neutral fst-delete" onClick={() => void askDelete()}>Delete form…</button>
        </div>
      </div>

      {/* The save bar (screen 22): only while something has changed. */}
      {dirty && (
        <SaveBar>
          <span className="fst-dirty" aria-live="polite">● Unsaved changes</span>
          <span style={{ flex: 1 }} />
          <button type="button" className="fs-btn neutral" disabled={saving} onClick={reset}>Discard</button>
          <button className="fs-btn primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save settings'}</button>
        </SaveBar>
      )}

      {duplicating && (
        <DuplicateFormDialog
          form={asSummary}
          onClose={() => setDuplicating(false)}
          onDone={(copy) => { setDuplicating(false); navigate(`/admin/forms/${copy.id}/build`); }}
        />
      )}
      {deleting && (
        <DeleteFormModal
          form={{ id: form.id, name: form.name, submission_count: deleting.submission_count }}
          onClose={() => setDeleting(null)}
          onDone={() => { toast(`“${form.name}” was deleted.`); navigate('/admin'); }}
          onError={(msg) => setError(msg)}
        />
      )}
      {guard.node}
    </form>
  );
}
